import { useEffect, useRef, useState, createContext, useContext, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  FileDown,
  Gauge,
  Globe,
  Play,
  Shield,
  Workflow,
  Wrench,
  Factory,
  Zap,
  Search,
} from "lucide-react";
import { ASSETS, LIVE_APP_URL, translate, SUPPORTED_LANGS } from "./i18n";
import ResourcesArticles from "./shiftlog/ResourcesArticles";
import ShiftlogArticlePage from "./ShiftlogArticlePage";
import "./styles/shiftlog-resources.css";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ----- i18n context -----
const I18nContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    const saved = window.localStorage.getItem("shiftlog.lang");
    if (saved && SUPPORTED_LANGS.includes(saved)) return saved;
    return "en";
  });

  const setLang = useCallback((l) => {
    setLangState(l);
    try {
      window.localStorage.setItem("shiftlog.lang", l);
      document.documentElement.lang = l;
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback((k, vars) => translate(lang, k, vars), [lang]);
  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
};

const useT = () => useContext(I18nContext);

// ----- helpers -----
const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

// ----- wordmark -----
const ShiftLogMark = ({ dark = false, size = "md" }) => {
  const sizes = {
    sm: "text-[15px]",
    md: "text-[17px]",
    lg: "text-xl",
  };
  return (
    <div className="flex items-center gap-2" data-testid="shiftlog-wordmark">
      <span
        className={`font-display ${sizes[size]} tracking-[0.18em] ${
          dark ? "text-black" : "text-white"
        }`}
        style={{ fontWeight: 900 }}
      >
        SHIFTLOG
      </span>
    </div>
  );
};

// ----- language switch -----
const LangSwitch = ({ variant = "dark" }) => {
  const { lang, setLang } = useT();
  const base = variant === "dark"
    ? "border-white/10 bg-white/[0.03] text-slate-300"
    : "border-black/10 bg-black/[0.03] text-slate-700";
  const activeDark = "bg-white text-black";
  const activeLight = "bg-black text-white";
  return (
    <div
      className={`inline-flex items-center gap-1 rounded-md border ${base} p-0.5`}
      data-testid="lang-switch"
    >
      <Globe className={`ml-1.5 h-3 w-3 ${variant === "dark" ? "text-slate-500" : "text-slate-500"}`} />
      {SUPPORTED_LANGS.map((l) => {
        const active = lang === l;
        return (
          <button
            key={l}
            onClick={() => setLang(l)}
            data-testid={`lang-${l}`}
            className={`rounded-[5px] px-2 py-1 text-[11px] font-medium uppercase tracking-widest transition-all ${
              active
                ? variant === "dark"
                  ? activeDark
                  : activeLight
                : "hover:text-current"
            }`}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
};

// ----- NAV -----
const Nav = () => {
  const { t } = useT();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "glass border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <a href="#top" className="flex items-center gap-2" data-testid="nav-logo">
          <ShiftLogMark size="sm" />
        </a>
        <nav className="hidden items-center gap-9 md:flex">
          {[
            ["nav.product", "product"],
            ["nav.features", "features"],
            ["nav.demo", "demo"],
            ["nav.pricing", "pricing"],
            ["nav.resources", "resources"],
          ].map(([key, id]) => (
            <button
              key={id}
              onClick={() => scrollToId(id)}
              data-testid={`nav-link-${id}`}
              className="text-sm text-slate-300 transition-colors hover:text-white"
            >
              {t(key)}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LangSwitch variant="dark" />
          <button
            onClick={() => scrollToId("contact")}
            data-testid="nav-cta-book-demo"
            className="group hidden items-center gap-1.5 rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-black transition-all hover:bg-amber-400 active:scale-[0.98] md:inline-flex"
          >
            {t("nav.bookDemo")}
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </header>
  );
};

// ----- HERO -----
const Hero = () => {
  const { t } = useT();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative isolate overflow-hidden bg-obsidian-950 pt-32 pb-20 md:pt-40 md:pb-28"
      data-testid="hero-section"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src="https://images.pexels.com/photos/34718931/pexels-photo-34718931.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
          alt=""
          aria-hidden
          className="h-full w-full object-cover opacity-[0.18]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black" />
        <div className="absolute inset-0 bg-grid opacity-60 radial-fade-b" />
        <div className="absolute inset-0 bg-grain opacity-[0.4] mix-blend-overlay" />
      </motion.div>
      <div className="pointer-events-none absolute left-1/2 top-36 -z-10 h-[520px] w-[720px] -translate-x-1/2 rounded-full bg-amber-500/20 blur-[140px]" />

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div {...fadeUp} className="flex items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/25 bg-amber-500/10 px-3 py-1 text-xs text-amber-300">
            <span className="h-1.5 w-1.5 animate-pulseDot rounded-full bg-amber-400" />
            <span className="font-mono tracking-[0.18em] uppercase">{t("hero.badge")}</span>
          </span>
        </motion.div>

        <div className="mt-8 grid items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.h1
              {...fadeUp}
              className="font-display text-[44px] leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-[74px]"
            >
              {t("hero.headline.a")}
              <br />
              <span className="text-amber-500 amber-text-glow">{t("hero.headline.b")}</span>
            </motion.h1>

            <motion.p
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-400"
            >
              {t("hero.sub")}
            </motion.p>

            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.18 }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <button
                onClick={() => scrollToId("contact")}
                data-testid="hero-cta-start-pilot"
                className="group inline-flex items-center gap-2 rounded-md bg-amber-500 px-5 py-3 text-sm font-semibold text-black transition-all hover:bg-amber-400 active:scale-[0.98] amber-glow"
              >
                {t("hero.cta.primary")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <a
                href={LIVE_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-book-demo"
                className="group inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/[0.03] px-5 py-3 text-sm font-semibold text-white backdrop-blur-sm transition-all hover:border-white/30 hover:bg-white/[0.07]"
              >
                <Play className="h-4 w-4 text-amber-400" />
                {t("hero.cta.secondary")}
                <ArrowUpRight className="h-4 w-4 text-slate-400 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <span className="ml-2 font-mono text-xs text-slate-500">{t("hero.minutes")}</span>
            </motion.div>

            {/* honest credibility line replaces fake logos + fake KPIs */}
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.28 }}
              className="mt-12 flex max-w-xl items-start gap-3 rounded-lg border border-white/5 bg-white/[0.02] p-5"
              data-testid="hero-credibility"
            >
              <div className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-md border border-amber-500/25 bg-amber-500/10">
                <Factory className="h-4 w-4 text-amber-400" />
              </div>
              <p className="text-sm leading-relaxed text-slate-300">{t("hero.credibility")}</p>
            </motion.div>
          </div>

          {/* Hero-side screenshot — real product image */}
          <motion.div
            initial={{ opacity: 0, y: 40, rotate: -1 }}
            whileInView={{ opacity: 1, y: 0, rotate: -1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="relative lg:col-span-5"
          >
            <div className="relative mx-auto w-full max-w-[340px]">
              <PhoneFrame src={ASSETS.screenshot_main} alt="ShiftLog shift dashboard" testId="hero-screenshot" />
              {/* floating mini live card */}
              <div className="absolute -bottom-6 -left-6 hidden w-[70%] rounded-lg border border-white/10 bg-obsidian-900/90 p-3 shadow-2xl backdrop-blur lg:block">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 animate-pulseDot rounded-full bg-emerald-400" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                    Live shift · Team A
                  </span>
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-display text-2xl text-white">2</span>
                  <span className="text-xs text-slate-400">open issues</span>
                </div>
                <div className="mt-1 text-[11px] text-slate-500">Shift 1 · now</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ----- Phone Frame displaying real mobile screenshot -----
const PhoneFrame = ({ src, alt, testId }) => {
  return (
    <div
      data-testid={testId}
      className="relative mx-auto aspect-[9/19] w-full overflow-hidden rounded-[32px] border border-white/10 bg-obsidian-900 p-2 shadow-[0_40px_120px_-20px_rgba(245,158,11,0.25)]"
    >
      <div className="absolute left-1/2 top-2 z-10 h-5 w-24 -translate-x-1/2 rounded-b-2xl bg-black/80" />
      <div className="relative h-full w-full overflow-hidden rounded-[26px] bg-white">
        <img src={src} alt={alt} className="h-full w-full object-cover object-top" />
      </div>
    </div>
  );
};

// ----- PRODUCT -----
const Product = () => {
  const { t } = useT();
  const shots = [
    { src: ASSETS.screenshot_main, titleKey: "product.shot1.title", descKey: "product.shot1.desc", Icon: ClipboardCheck },
    { src: ASSETS.screenshot_search, titleKey: "product.shot2.title", descKey: "product.shot2.desc", Icon: Search },
    { src: ASSETS.screenshot_pdf, titleKey: "product.shot3.title", descKey: "product.shot3.desc", Icon: FileDown },
  ];

  return (
    <section
      id="product"
      className="relative bg-obsidian-950 py-28"
      data-testid="product-section"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div {...fadeUp} className="max-w-2xl">
          <span className="num-tag">{t("product.num")}</span>
          <h2 className="mt-3 font-display text-4xl leading-tight text-white md:text-5xl">
            {t("product.title.a")} <span className="text-amber-500">{t("product.title.b")}</span>{" "}
            {t("product.title.c")}
          </h2>
          <p className="mt-5 text-slate-400">{t("product.sub")}</p>
        </motion.div>

        <div className="mt-14 grid gap-10 md:grid-cols-3">
          {shots.map(({ src, titleKey, descKey, Icon }, i) => (
            <motion.div
              key={titleKey}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`product-shot-${i + 1}`}
            >
              <PhoneFrame src={src} alt={t(titleKey)} testId={`product-shot-phone-${i + 1}`} />
              <div className="mt-6 flex items-start gap-3">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-amber-500/25 bg-amber-500/10">
                  <Icon className="h-4 w-4 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-display text-lg text-white">{t(titleKey)}</h3>
                  <p className="mt-1 text-sm text-slate-400">{t(descKey)}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----- FEATURES (dark gray) -----
const Features = () => {
  const { t } = useT();
  return (
    <section
      id="features"
      className="relative bg-[#0c0c10] py-28"
      data-testid="features-section"
    >
      <div className="absolute inset-0 bg-grid-tight opacity-20 radial-fade-b" />
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div {...fadeUp} className="mb-12 max-w-2xl">
          <span className="num-tag">{t("features.num")}</span>
          <h2 className="mt-3 font-display text-4xl leading-tight text-white md:text-5xl">
            {t("features.title")}
          </h2>
        </motion.div>

        <div className="grid gap-5 md:grid-cols-6">
          <motion.div
            {...fadeUp}
            className="group relative col-span-6 overflow-hidden rounded-2xl border border-white/10 bg-obsidian-900 p-8 md:col-span-4"
          >
            <div className="bg-grid absolute inset-0 opacity-20" />
            <div className="relative">
              <Workflow className="h-8 w-8 text-amber-500" />
              <h3 className="mt-5 font-display text-2xl text-white">{t("features.f1.title")}</h3>
              <p className="mt-3 max-w-lg text-slate-400">{t("features.f1.desc")}</p>
              <div className="mt-8 space-y-2 font-mono text-xs text-slate-300">
                <div className="tick-marker">{t("features.f1.b1")}</div>
                <div className="tick-marker">{t("features.f1.b2")}</div>
                <div className="tick-marker">{t("features.f1.b3")}</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            {...fadeUp}
            className="relative col-span-6 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-amber-500/20 via-amber-600/10 to-transparent p-8 md:col-span-2"
          >
            <BarChart3 className="h-8 w-8 text-amber-400" />
            <h3 className="mt-5 font-display text-xl text-white">{t("features.f2.title")}</h3>
            <p className="mt-3 text-sm text-slate-300/90">{t("features.f2.desc")}</p>
          </motion.div>

          <motion.div
            {...fadeUp}
            className="col-span-6 rounded-2xl border border-white/10 bg-obsidian-900 p-8 md:col-span-2"
          >
            <Shield className="h-7 w-7 text-amber-500" />
            <h3 className="mt-4 font-display text-xl text-white">{t("features.f3.title")}</h3>
            <p className="mt-2 text-sm text-slate-400">{t("features.f3.desc")}</p>
          </motion.div>

          <motion.div
            {...fadeUp}
            className="col-span-6 rounded-2xl border border-white/10 bg-obsidian-900 p-8 md:col-span-2"
          >
            <Gauge className="h-7 w-7 text-amber-500" />
            <h3 className="mt-4 font-display text-xl text-white">{t("features.f4.title")}</h3>
            <p className="mt-2 text-sm text-slate-400">{t("features.f4.desc")}</p>
          </motion.div>

          <motion.div
            {...fadeUp}
            className="col-span-6 rounded-2xl border border-white/10 bg-obsidian-900 p-8 md:col-span-2"
          >
            <Zap className="h-7 w-7 text-amber-500" />
            <h3 className="mt-4 font-display text-xl text-white">{t("features.f5.title")}</h3>
            <p className="mt-2 text-sm text-slate-400">{t("features.f5.desc")}</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ----- DEMO (dark) with real video -----
const Demo = () => {
  const { t } = useT();
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const onPlay = () => {
    setPlaying(true);
    // use timeout so the <video> element exists before play()
    setTimeout(() => {
      videoRef.current?.play?.().catch(() => {});
    }, 0);
  };

  return (
    <section id="demo" className="relative bg-obsidian-950 py-28" data-testid="demo-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-end">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <span className="num-tag">{t("demo.num")}</span>
            <h2 className="mt-3 font-display text-4xl leading-tight text-white md:text-5xl">
              {t("demo.title")}
            </h2>
            <p className="mt-5 text-slate-400">{t("demo.sub")}</p>
            <button
              onClick={() => scrollToId("contact")}
              data-testid="demo-cta-book"
              className="mt-7 inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/[0.03] px-5 py-3 text-sm font-semibold text-white transition-all hover:border-white/30 hover:bg-white/[0.07]"
            >
              {t("demo.liveCta")}
              <ArrowUpRight className="h-4 w-4 text-amber-400" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
            data-testid="demo-video-slot"
          >
            <div className="group relative aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black shadow-[0_30px_80px_-20px_rgba(245,158,11,0.25)]">
              {playing ? (
                <video
                  ref={videoRef}
                  src={ASSETS.video}
                  controls
                  autoPlay
                  playsInline
                  className="h-full w-full object-cover"
                  data-testid="demo-video-el"
                />
              ) : (
                <>
                  <video
                    src={ASSETS.video}
                    preload="metadata"
                    muted
                    playsInline
                    className="h-full w-full object-cover opacity-60"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/30 to-transparent" />
                  <button
                    type="button"
                    onClick={onPlay}
                    data-testid="demo-video-play"
                    className="absolute left-1/2 top-1/2 flex h-24 w-24 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-amber-500 text-black transition-all hover:scale-105 hover:bg-amber-400 amber-glow"
                    aria-label="Play demo video"
                  >
                    <Play className="h-10 w-10 translate-x-[2px] fill-black" strokeWidth={0} />
                  </button>
                  <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                    <div>
                      <div className="font-display text-xl text-white">ShiftLog · Demo v3</div>
                      <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
                        {t("demo.num")}
                      </div>
                    </div>
                    <div className="hidden items-center gap-2 rounded-md border border-white/10 bg-black/60 px-3 py-1.5 text-xs text-slate-300 backdrop-blur md:flex">
                      <Clock className="h-3.5 w-3.5 text-amber-400" />
                      Demo
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ----- FOUNDER (light) -----
const Founder = () => {
  const { t } = useT();
  return (
    <section
      id="founder"
      className="relative bg-[#F5F3EE] py-28 text-slate-900"
      data-testid="founder-section"
    >
      <div className="mx-auto max-w-6xl px-6 lg:px-10">
        <div className="grid gap-14 lg:grid-cols-12 lg:items-center">
          {/* Real founder photo */}
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div
              data-testid="founder-photo"
              className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-black/10 bg-slate-900 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.25)]"
            >
              <img
                src={ASSETS.founder}
                alt="Jarek Borowiec, founder of ShiftLog"
                className="h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/80">
                  Jarek Borowiec
                </div>
                <div className="mt-0.5 text-[11px] text-white/60">
                  Founder · ShiftLog
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-7">
            <span className="num-tag-light">{t("founder.num")}</span>
            <h2 className="mt-3 font-display text-4xl leading-tight text-slate-900 md:text-[44px]">
              {t("founder.title")}
            </h2>
            <div className="mt-6 space-y-4 text-lg leading-relaxed text-slate-700">
              {t("founder.p").split("\n").map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
            <div className="mt-8 flex items-center gap-3">
              <div className="h-px w-10 bg-amber-500" />
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                {t("founder.signature")}
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ----- PRICING (light) -----
const Pricing = () => {
  const { t } = useT();
  const plans = [
    {
      id: "standard",
      name: t("pricing.standard.name"),
      price: t("pricing.standard.price"),
      per: t("pricing.perMonth"),
      bullets: [
        t("pricing.standard.b1"),
        t("pricing.standard.b2"),
        t("pricing.standard.b3"),
        t("pricing.standard.b4"),
        t("pricing.standard.b5"),
        t("pricing.standard.b6"),
      ],
      cta: t("pricing.cta"),
      highlight: false,
    },
    {
      id: "pro",
      name: t("pricing.pro.name"),
      price: t("pricing.pro.price"),
      per: t("pricing.perMonth"),
      tag: t("pricing.pro.tag"),
      bullets: [
        t("pricing.pro.b1"),
        t("pricing.pro.b2"),
        t("pricing.pro.b3"),
        t("pricing.pro.b4"),
        t("pricing.pro.b5"),
        t("pricing.pro.b6"),
      ],
      cta: t("pricing.cta"),
      highlight: true,
    },
    {
      id: "enterprise",
      name: t("pricing.ent.name"),
      price: t("pricing.ent.price"),
      per: "",
      bullets: [
        t("pricing.ent.b1"),
        t("pricing.ent.b2"),
        t("pricing.ent.b3"),
        t("pricing.ent.b4"),
        t("pricing.ent.b5"),
        t("pricing.ent.b6"),
      ],
      cta: t("pricing.cta.ent"),
      highlight: false,
    },
  ];

  return (
    <section
      id="pricing"
      className="relative bg-white py-28 text-slate-900"
      data-testid="pricing-section"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div {...fadeUp} className="mb-14 max-w-2xl">
          <span className="num-tag-light">{t("pricing.num")}</span>
          <h2 className="mt-3 font-display text-4xl leading-tight text-slate-900 md:text-5xl">
            {t("pricing.title")}
          </h2>
          <p className="mt-4 text-slate-600">{t("pricing.sub")}</p>
        </motion.div>

        <div className="grid gap-6 md:grid-cols-3">
          {plans.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`pricing-card-${p.id}`}
              className={`relative rounded-2xl border p-8 transition-all ${
                p.highlight
                  ? "border-slate-900 bg-slate-900 text-white shadow-xl"
                  : "border-slate-200 bg-white text-slate-900 hover:border-slate-300"
              }`}
            >
              {p.tag && (
                <span className="absolute -top-3 left-8 rounded-full bg-amber-500 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-black">
                  {p.tag}
                </span>
              )}
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] opacity-60">
                {p.name}
              </div>
              <div className="mt-5 flex items-baseline gap-1">
                <span className="font-display text-5xl" style={{ letterSpacing: "-0.04em" }}>
                  {p.price}
                </span>
                {p.per && (
                  <span
                    className={`text-sm ${
                      p.highlight ? "text-slate-400" : "text-slate-500"
                    }`}
                  >
                    {p.per}
                  </span>
                )}
              </div>
              <ul className="mt-7 space-y-3 text-sm">
                {p.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5">
                    <CheckCircle2
                      className={`mt-0.5 h-4 w-4 shrink-0 ${
                        p.highlight ? "text-amber-400" : "text-amber-500"
                      }`}
                    />
                    <span className={p.highlight ? "text-slate-200" : "text-slate-700"}>{b}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => scrollToId("contact")}
                data-testid={`pricing-cta-${p.id}`}
                className={`mt-8 w-full rounded-md px-4 py-3 text-sm font-semibold transition-all ${
                  p.highlight
                    ? "bg-amber-500 text-black hover:bg-amber-400"
                    : "border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white"
                }`}
              >
                {p.cta}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----- RESOURCES (light gray) -----
const Resources = () => {
  const { t } = useT();
  const items = [
    {
      id: "sap",
      tag: t("resources.sap.tag"),
      title: t("resources.sap.title"),
      desc: t("resources.sap.desc"),
      meta: t("resources.sap.meta"),
      cta: t("resources.sap.cta"),
      Icon: FileDown,
    },
    {
      id: "lean",
      tag: t("resources.lean.tag"),
      title: t("resources.lean.title"),
      desc: t("resources.lean.desc"),
      meta: t("resources.lean.meta"),
      cta: t("resources.lean.cta"),
      Icon: Wrench,
    },
  ];
  return (
    <section
      id="resources"
      className="relative bg-[#EFECE4] py-28 text-slate-900"
      data-testid="resources-section"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
<ResourcesArticles />
        <motion.div {...fadeUp} className="mb-10 max-w-2xl">
          <span className="num-tag-light">{t("resources.num")}</span>
          <h2 className="mt-3 font-display text-3xl leading-tight text-slate-900 md:text-4xl">
            {t("resources.title")}
          </h2>
          <p className="mt-3 text-slate-600">{t("resources.sub")}</p>
        </motion.div>

        <div className="grid gap-6 md:grid-cols-2">
          {items.map(({ id, tag, title, desc, meta, cta, Icon }, i) => (
            <motion.a
              key={id}
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("contact");
              }}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="group relative block overflow-hidden rounded-2xl border border-black/10 bg-white p-8 transition-all hover:-translate-y-1 hover:border-slate-400 hover:shadow-xl"
              data-testid={`resource-card-${id}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                  {tag}
                </span>
                <ArrowUpRight className="h-5 w-5 text-slate-400 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-amber-500" />
              </div>
              <div className="mt-6 flex items-start gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-lg border border-black/10 bg-slate-50">
                  <Icon className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <h3 className="font-display text-2xl text-slate-900">{title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{desc}</p>
                </div>
              </div>
              <div className="mt-8 flex items-center justify-between border-t border-black/5 pt-5">
                <span className="font-mono text-xs uppercase tracking-widest text-slate-500">
                  {meta}
                </span>
                <span className="text-sm font-semibold text-amber-600 transition-colors group-hover:text-amber-700">
                  {cta} →
                </span>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----- CONTACT (dark) -----
const ContactForm = () => {
  const { t } = useT();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    company: "",
    team_size: "",
    message: "",
    intent: "book_demo",
  });
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", error: "" });
    try {
      const payload = { ...form };
      if (!payload.team_size) delete payload.team_size;
      if (!payload.message) delete payload.message;
      await axios.post(`${API}/contact`, payload);
      setStatus({ state: "success", error: "" });
      setForm({
        first_name: "",
        last_name: "",
        email: "",
        company: "",
        team_size: "",
        message: "",
        intent: form.intent,
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setStatus({ state: "error", error: String(detail) });
    }
  };

  return (
    <section
      id="contact"
      className="relative border-t border-white/5 bg-gradient-to-b from-black via-obsidian-900 to-black py-28"
      data-testid="contact-section"
    >
      <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-5">
          <span className="num-tag">{t("contact.num")}</span>
          <h2 className="mt-3 font-display text-4xl leading-tight text-white md:text-5xl">
            {t("contact.title")}
          </h2>
          <p className="mt-5 max-w-md text-slate-400">{t("contact.sub")}</p>

          <ul className="mt-10 space-y-4 text-sm text-slate-300">
            {["contact.b1", "contact.b2", "contact.b3"].map((k) => (
              <li key={k} className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                {t(k)}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="rounded-2xl border border-white/10 bg-obsidian-900/70 p-6 backdrop-blur md:p-8"
          >
            <div className="mb-6 inline-flex rounded-md border border-white/10 bg-black/40 p-1">
              {[
                ["book_demo", t("contact.tab.demo")],
                ["start_pilot", t("contact.tab.pilot")],
              ].map(([val, label]) => {
                const active = form.intent === val;
                return (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, intent: val }))}
                    data-testid={`intent-${val}`}
                    className={`rounded-[5px] px-4 py-1.5 text-sm font-medium transition-all ${
                      active ? "bg-amber-500 text-black" : "text-slate-300 hover:text-white"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <Field label={t("contact.firstName")} required>
                <input
                  required
                  type="text"
                  value={form.first_name}
                  onChange={update("first_name")}
                  data-testid="input-first-name"
                  className={inputCls}
                  placeholder="Alex"
                />
              </Field>
              <Field label={t("contact.lastName")} required>
                <input
                  required
                  type="text"
                  value={form.last_name}
                  onChange={update("last_name")}
                  data-testid="input-last-name"
                  className={inputCls}
                  placeholder="Morgan"
                />
              </Field>
              <Field label={t("contact.email")} required>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="input-email"
                  className={inputCls}
                  placeholder="alex@yourcompany.com"
                />
              </Field>
              <Field label={t("contact.company")} required>
                <input
                  required
                  type="text"
                  value={form.company}
                  onChange={update("company")}
                  data-testid="input-company"
                  className={inputCls}
                  placeholder="Acme Industries"
                />
              </Field>
              <Field label={t("contact.teamSize")}>
                <select
                  value={form.team_size}
                  onChange={update("team_size")}
                  data-testid="input-team-size"
                  className={inputCls}
                >
                  <option value="">{t("contact.teamSize.placeholder")}</option>
                  <option value="1-50">1 – 50</option>
                  <option value="51-200">51 – 200</option>
                  <option value="201-1000">201 – 1 000</option>
                  <option value="1000+">1 000+</option>
                </select>
              </Field>
              <Field label={t("contact.plant")}>
                <input
                  type="text"
                  value={form.message}
                  onChange={update("message")}
                  data-testid="input-message"
                  className={inputCls}
                  placeholder={t("contact.plant.placeholder")}
                />
              </Field>
            </div>

            <div className="mt-7 flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
              <p className="text-xs text-slate-500">{t("contact.legal")}</p>
              <button
                type="submit"
                disabled={status.state === "loading"}
                data-testid="contact-submit"
                className="group inline-flex items-center gap-2 rounded-md bg-amber-500 px-6 py-3 text-sm font-semibold text-black transition-all hover:bg-amber-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 amber-glow"
              >
                {status.state === "loading"
                  ? t("contact.submitting")
                  : form.intent === "start_pilot"
                  ? t("contact.submit.pilot")
                  : t("contact.submit.demo")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>

            {status.state === "success" && (
              <div
                data-testid="contact-success"
                className="mt-5 flex items-start gap-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-300"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <div className="font-semibold text-emerald-200">
                    {t("contact.success.title")}
                  </div>
                  <div className="text-emerald-300/80">{t("contact.success.msg")}</div>
                </div>
              </div>
            )}
            {status.state === "error" && (
              <div
                data-testid="contact-error"
                className="mt-5 flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>{status.error}</div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

const inputCls =
  "w-full rounded-md border border-white/10 bg-black/40 px-3.5 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none transition-all focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/25";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">
      {label} {required && <span className="text-amber-500">*</span>}
    </span>
    {children}
  </label>
);

// ----- FOOTER (dark, cleaned) -----
const Footer = () => {
  const { t } = useT();
  const year = new Date().getFullYear();
  const sectionIds = {
    "footer.link.product": "product",
    "footer.link.demo": "demo",
    "footer.link.pricing": "pricing",
    "footer.link.sap": "resources",
    "footer.link.lean": "resources",
    "footer.link.contact": "contact",
    "footer.link.security": "contact",
  };
  const cols = [
    {
      headKey: "footer.product",
      links: ["footer.link.product", "footer.link.demo", "footer.link.pricing", "footer.link.security"],
    },
    {
      headKey: "footer.resources",
      links: ["footer.link.sap", "footer.link.lean"],
    },
    {
      headKey: "footer.company",
      links: ["footer.link.contact"],
    },
  ];

  return (
    <footer className="border-t border-white/5 bg-black py-14">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <ShiftLogMark size="md" />
            <p className="mt-5 max-w-sm text-sm text-slate-500">{t("footer.tagline")}</p>
            <div className="mt-6">
              <LangSwitch variant="dark" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-8 md:col-span-7 md:grid-cols-3">
            {cols.map(({ headKey, links }) => (
              <div key={headKey}>
                <div className="mb-4 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                  {t(headKey)}
                </div>
                <ul className="space-y-2">
                  {links.map((k) => (
                    <li key={k}>
                      <button
                        onClick={() => scrollToId(sectionIds[k])}
                        data-testid={`footer-${k.split(".").pop()}`}
                        className="text-sm text-slate-400 transition-colors hover:text-white"
                      >
                        {t(k)}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/5 pt-8 text-xs text-slate-600 md:flex-row md:items-center">
          <div className="flex items-center gap-2 font-mono uppercase tracking-widest">
            <Factory className="h-3.5 w-3.5" />
            {t("footer.copyright", { year })}
          </div>
        </div>
      </div>
    </footer>
  );
};

// ----- APP -----
function App() {
   const path = window.location.pathname;

  useEffect(() => {
    axios.get(`${API}/`).catch(() => {});
  }, []);

  if (path.startsWith("/resources/")) {
    return (
      <I18nProvider>
        <ShiftlogArticlePage />
      </I18nProvider>
    );
  }

  return (
    <I18nProvider>
      <div className="App min-h-screen bg-obsidian-950 text-white" data-testid="app-root">
        <Nav />
        <main>
          <Hero />
          <Product />
          <Features />
          <Demo />
          <Founder />
          <Pricing />
          <Resources />
          <ContactForm />
        </main>
        <Footer />
      </div>
    </I18nProvider>
  );
}

export default App;
